import React, { useMemo } from 'react';
import {
  CellTypes,
  DataGrid,
  DataGridProps,
  GridColDef,
} from '@fashionnovateam/nova-ui-toolkit';
import numeral from 'numeral';
import { sumBy } from 'lodash';
import { InvoiceItem, BillCreditLineItem } from '../../Lib/Api/vendor';

type Row = {
  id: string;
  item: CellTypes.DataGridCellStringParams;
  description: CellTypes.DataGridCellStringParams;
  quantity: CellTypes.DataGridCellStringParams;
  rate: CellTypes.DataGridCellStringParams;
  amount: CellTypes.DataGridCellStringParams;
};

const columns: GridColDef[] = [
  {
    field: 'item',
    headerName: 'Item',
    render: 'string',
    display: 'flex',
    flex: 4,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'description',
    headerName: 'Description',
    display: 'flex',
    render: 'string',
    flex: 3,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'quantity',
    headerName: 'Quantity',
    display: 'flex',
    render: 'string',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'rate',
    headerName: 'Rate',
    display: 'flex',
    render: 'string',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'amount',
    headerName: 'Amount',
    display: 'flex',
    render: 'string',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
];

type Props = {
  rows: InvoiceItem[] | BillCreditLineItem[];
  isBillCredit: boolean;
} & Omit<DataGridProps<Row>, 'columns' | 'rows'>;

function VendorsDataGrid({ rows, isBillCredit, ...dataGridProps }: Props) {
  const transformedRows = useMemo(
    () =>
      rows.map(
        (lineItem) =>
          ({
            id: lineItem.id,
            item: lineItem.item ?? '',
            description: lineItem.description ?? '',
            quantity: numeral(lineItem.quantity).format('0,0'),
            rate:
              lineItem.item !== 'RTV Fee'
                ? numeral(lineItem.rate).format('$0,0.00')
                : `${lineItem.rate}%`,
            amount: numeral(lineItem.amount).format('$0,0.00'),
          }) satisfies Row,
      ),
    [rows],
  );

  const quantityAggregation = {
    apply: () =>
      numeral(sumBy<InvoiceItem | BillCreditLineItem>(rows, 'quantity')).format(
        '0,0',
      ),
    label: '',
  };

  const amountAggregation = {
    apply: () =>
      isBillCredit
        ? numeral(
            sumBy<InvoiceItem | BillCreditLineItem>(rows, (item) => {
              if (item.item === 'RTV Fee') return item.amount ?? 0;
              if (item.item?.includes('Chargeback')) return item.amount ?? 0;
              return (item.rate ?? 0) * (item.quantity ?? 0);
            }),
          ).format('$0,0.00')
        : numeral(
            sumBy<InvoiceItem | BillCreditLineItem>(rows, 'amount'),
          ).format('$0,0.00'),
    label: '',
  };

  const itemAggregation = {
    apply: () => 'Total',
    label: '',
  };

  return (
    <DataGrid
      hideFilterButton
      hideGroupButton
      rows={transformedRows}
      paginationMode="client"
      pageSizeOptions={[15]}
      sortingMode="client"
      disableAggregation={false}
      autoHeight
      initialState={{
        aggregation: {
          model: {
            item: 'item',
            quantity: 'quantity',
            amount: 'amount',
          },
        },
        pinnedColumns: {
          left: ['item'],
        },
      }}
      {...dataGridProps}
      aggregationFunctions={{
        item: itemAggregation,
        amount: amountAggregation,
        quantity: quantityAggregation,
      }}
      columns={columns}
    />
  );
}

export default VendorsDataGrid;
